// === Section: MoveHistoryDialog Component ===
// File: src/components/MoveHistoryDialog.js
// Description: Dialog for displaying move history in a 4x6 squares, left-aligned, scrollable, with close button.
import React, { useEffect, useRef } from 'react';
import pcModule from './MoveHistoryDialog.pc.module.css';
import mobileModule from './MoveHistoryDialog.mobile.module.css';
import useSelectedCssModule from '../hooks/useSelectedCssModule';
import { logDomEvent, isLoggerPortEnabled } from '../utils/spaLogger';

function MoveHistoryDialog({ moves, onClose, loggerTogglesLoaded }) {
  const contentRef = useRef(null);
  // === Section: Select CSS Module for Device ===
  const styles = useSelectedCssModule(pcModule, mobileModule);
  // === End Section: Select CSS Module for Device ===

  // Log when dialog is opened
  useEffect(() => {
    if (!loggerTogglesLoaded) return;
    if (contentRef.current) {
      const count = Array.isArray(moves) ? moves.length : 0;
      const domHtml = contentRef.current.outerHTML;
      const element = contentRef.current.className || contentRef.current.tagName;
      if (isLoggerPortEnabled('MoveHistoryDialog')) {
        logDomEvent(
          'MoveHistoryDialog',
          'move-history-dialog-open',
          element,
          domHtml,
          contentRef.current
        );
      }
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggerTogglesLoaded]);

  // Log scroll events
  const handleScroll = () => {
    if (!loggerTogglesLoaded) return;
    if (contentRef.current) {
      const count = Array.isArray(moves) ? moves.length : 0;
      const domHtml = contentRef.current.outerHTML;
      const element = contentRef.current.className || contentRef.current.tagName;
      if (isLoggerPortEnabled('MoveHistoryDialog')) {
        logDomEvent(
          'MoveHistoryDialog',
          'move-history-dialog-scroll',
          element,
          domHtml,
          contentRef.current
        );
      }
    }
  };

  // Log close button
  const handleClose = () => {
    if (!loggerTogglesLoaded) {
      if (onClose) onClose();
      return;
    }
    if (contentRef.current) {
      const count = Array.isArray(moves) ? moves.length : 0;
      const domHtml = contentRef.current.outerHTML;
      const element = contentRef.current.className || contentRef.current.tagName;
      if (isLoggerPortEnabled('MoveHistoryDialog')) {
        logDomEvent(
          'MoveHistoryDialog',
          'move-history-dialog-close',
          element,
          domHtml,
          contentRef.current
        );
      }
    }
    if (onClose) onClose();
  };
  if (!loggerTogglesLoaded) return null;
  return (
    <div className={styles.moveHistoryDialogOverlay}>
      <div className={styles.moveHistoryDialog}>
        <div className={styles.moveHistoryDialogHeader}>
          <span className={styles.moveHistoryDialogTitle}>Move History</span>
          <button className={styles.closeButton} onClick={handleClose} aria-label="Close move history">×</button>
        </div>
        <div
          className={styles.moveHistoryDialogContent}
          ref={contentRef}
          onScroll={handleScroll}
          style={{ overflowY: 'auto', maxHeight: '300px' }}
        >
          {moves && moves.length > 0 ? (
            <ul className={styles.moveHistoryList}>
              {moves.map((move, idx) => (
                <li key={idx} className={styles.moveHistoryItem}>{move}</li>
              ))}
            </ul>
          ) : (
            <div className={styles.moveHistoryEmpty}>No moves yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MoveHistoryDialog;
// === End Section: MoveHistoryDialog Component ===
